import React, { useState, useEffect, useMemo } from 'react';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiPieChart,
  FiBarChart2,
  FiTarget,
  FiAlertCircle,
  FiCheckCircle,
  FiCalendar,
  FiDollarSign,
  FiArrowUp,
  FiArrowDown,
  FiRefreshCw,
} from 'react-icons/fi';
import { HiLightBulb } from 'react-icons/hi';
import { dashboardService, transactionsService } from '../services';
import api from '../services/api';
import type { MonthlyExpenses, Transaction } from '../types';
import { Card, LoaderComponent } from '../components/ui';

// Format currency in ECV
const formatCurrency = (value: number | undefined | null): string => {
  const safe = typeof value === 'number' && !isNaN(value) ? value : 0;
  return new Intl.NumberFormat('pt-CV', {
    style: 'currency',
    currency: 'CVE',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(safe);
};

// Category colors for charts
const CATEGORY_COLORS: { [key: string]: string } = {
  agua: '#3B82F6',
  eletricidade: '#F59E0B',
  telecomunicacoes: '#10B981',
  internet: '#8B5CF6',
  tv: '#EF4444',
  televisao: '#EF4444',
  habitacao: '#14B8A6',
  comercio: '#EC4899',
  saude: '#F43F5E',
  seguros: '#6366F1',
  governo: '#64748B',
  educacao: '#F59E0B',
  transporte: '#06B6D4',
  outros: '#9CA3AF',
};

// Get color for category
const getCategoryColor = (category: string): string => {
  const normalized = category
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  for (const key of Object.keys(CATEGORY_COLORS)) {
    if (normalized.includes(key)) {
      return CATEGORY_COLORS[key];
    }
  }
  return CATEGORY_COLORS.outros;
};

// Smart tips based on spending patterns
interface SmartTip {
  id: string;
  type: 'success' | 'warning' | 'info';
  title: string;
  message: string;
  icon: React.ReactNode;
}

const generateSmartTips = (
  expenses: MonthlyExpenses | null,
  previousExpenses: MonthlyExpenses | null,
  transactions: Transaction[]
): SmartTip[] => {
  const tips: SmartTip[] = [];

  if (!expenses) return tips;

  // Tip 1: Compare with previous month
  if (previousExpenses) {
    const diff = expenses.total - previousExpenses.total;
    const percentage = previousExpenses.total > 0
      ? Math.abs((diff / previousExpenses.total) * 100).toFixed(0)
      : 0;

    if (diff > 0) {
      tips.push({
        id: 'spending-increase',
        type: 'warning',
        title: 'Aumento de Gastos',
        message: `Os seus gastos aumentaram ${percentage}% em relação ao mês anterior. Considere rever as suas despesas.`,
        icon: <FiTrendingUp className="text-amber-500" />,
      });
    } else if (diff < 0) {
      tips.push({
        id: 'spending-decrease',
        type: 'success',
        title: 'Parabens!',
        message: `Conseguiu reduzir os seus gastos em ${percentage}% este mes. Continue assim!`,
        icon: <FiTrendingDown className="text-green-500" />,
      });
    }
  }

  // Tip 2: Highest spending category
  if (expenses.categorias && expenses.categorias.length > 0) {
    const highest = expenses.categorias.reduce((max, cat) =>
      cat.valor > max.valor ? cat : max
    );
    if (highest.percentagem > 40) {
      tips.push({
        id: 'high-category',
        type: 'info',
        title: 'Categoria Dominante',
        message: `${highest.categoria} representa ${highest.percentagem.toFixed(0)}% dos seus gastos. Avalie se pode otimizar este custo.`,
        icon: <FiPieChart className="text-blue-500" />,
      });
    }
  }

  // Tip 3: Regular payment reminder
  const txArr = Array.isArray(transactions) ? transactions : [];
  const regularPayments = txArr.filter(t =>
    t.tipoServico === 'WEBSR' || t.servico?.toLowerCase().includes('agua') || t.servico?.toLowerCase().includes('eletricidade')
  );
  if (regularPayments.length > 0) {
    tips.push({
      id: 'regular-payments',
      type: 'info',
      title: 'Pagamentos Regulares',
      message: 'Considere configurar pagamentos automaticos para evitar atrasos e multas.',
      icon: <FiRefreshCw className="text-purple-500" />,
    });
  }

  // Tip 4: Budget suggestion
  tips.push({
    id: 'budget-tip',
    type: 'info',
    title: 'Dica de Poupanca',
    message: 'Tente guardar pelo menos 10-20% do seu rendimento mensal para emergencias.',
    icon: <FiTarget className="text-indigo-500" />,
  });

  // Tip 5: Low spending congratulation
  if (expenses.total < 50000) {
    tips.push({
      id: 'low-spending',
      type: 'success',
      title: 'Gastos Controlados',
      message: 'Os seus gastos estao num nivel saudavel este mes. Mantenha o bom trabalho!',
      icon: <FiCheckCircle className="text-green-500" />,
    });
  }

  return tips.slice(0, 4); // Return max 4 tips
};

// Category Bar Component
const CategoryBar: React.FC<{ categoria: string; valor: number; percentagem: number; total: number }> = ({
  categoria,
  valor,
  percentagem,
}) => {
  const color = getCategoryColor(categoria);

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium text-pagali-gray-700">{categoria}</span>
        <span className="text-sm text-pagali-gray-500">{formatCurrency(valor)}</span>
      </div>
      <div className="w-full bg-pagali-gray-100 rounded-full h-3">
        <div
          className="h-3 rounded-full transition-all duration-500"
          style={{
            width: `${Math.min(percentagem, 100)}%`,
            backgroundColor: color,
          }}
        />
      </div>
      <p className="text-xs text-pagali-gray-400 mt-1">{(percentagem || 0).toFixed(1)}% do total</p>
    </div>
  );
};

// Stat Card Component
const StatCard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  color?: string;
}> = ({ title, value, subtitle, icon, trend, trendValue, color = 'bg-pagali-primary' }) => (
  <Card className="relative overflow-hidden">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-pagali-gray-500 mb-1">{title}</p>
        <p className="text-2xl font-bold text-pagali-gray-800">{value}</p>
        {subtitle && <p className="text-xs text-pagali-gray-400 mt-1">{subtitle}</p>}
        {trend && trendValue && (
          <div className={`flex items-center mt-2 text-sm ${
            trend === 'up' ? 'text-red-500' : trend === 'down' ? 'text-green-500' : 'text-pagali-gray-500'
          }`}>
            {trend === 'up' ? <FiArrowUp className="mr-1" /> : trend === 'down' ? <FiArrowDown className="mr-1" /> : null}
            {trendValue}
          </div>
        )}
      </div>
      <div className={`${color} p-3 rounded-xl text-white`}>
        {icon}
      </div>
    </div>
  </Card>
);

// Monthly Selector Component
const MonthSelector: React.FC<{
  month: number;
  year: number;
  onChange: (month: number, year: number) => void;
}> = ({ month, year, onChange }) => {
  const months = [
    'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const handlePrevious = () => {
    if (month === 1) {
      onChange(12, year - 1);
    } else {
      onChange(month - 1, year);
    }
  };

  const handleNext = () => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      if (month === 12) {
        onChange(1, year + 1);
      } else {
        onChange(month + 1, year);
      }
    }
  };

  return (
    <div className="flex items-center gap-4 bg-white rounded-xl p-2 shadow-sm border border-pagali-gray-100">
      <button
        onClick={handlePrevious}
        className="p-2 hover:bg-pagali-gray-50 rounded-lg transition-colors"
      >
        <FiArrowDown className="rotate-90 text-pagali-gray-600" />
      </button>
      <div className="flex items-center gap-2">
        <FiCalendar className="text-pagali-primary" />
        <span className="font-medium text-pagali-gray-700">
          {months[month - 1]} {year}
        </span>
      </div>
      <button
        onClick={handleNext}
        className="p-2 hover:bg-pagali-gray-50 rounded-lg transition-colors"
      >
        <FiArrowUp className="rotate-90 text-pagali-gray-600" />
      </button>
    </div>
  );
};

// History data
interface HistoryData {
  yearly: { ano: number; transacoes: number; gasto: number }[];
  monthly: { mes: string; transacoes: number; gasto: number }[];
  topCategories: { name: string; transacoes: number; gasto: number }[];
  totals: { gastoTotal: number; totalTransacoes: number; primeiroAno: number; anosAtivo: number; mediaAnual: number; mediaMensal: number };
}

// Main Insights Page
const Insights: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [expenses, setExpenses] = useState<MonthlyExpenses | null>(null);
  const [previousExpenses, setPreviousExpenses] = useState<MonthlyExpenses | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [history, setHistory] = useState<HistoryData | null>(null);
  const [activeTab, setActiveTab] = useState<'mensal' | 'historico'>('mensal');

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load current month expenses
        const currentExpenses = await dashboardService.getMonthlyExpenses(currentMonth, currentYear);
        setExpenses(currentExpenses);

        // Load previous month for comparison
        const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear;
        try {
          const prev = await dashboardService.getMonthlyExpenses(prevMonth, prevYear);
          setPreviousExpenses(prev);
        } catch {
          setPreviousExpenses(null);
        }

        // Load transactions for the period — calcular último dia correcto
        const startDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
        const lastDay = new Date(currentYear, currentMonth, 0).getDate();
        const endDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        try {
          const txns = await transactionsService.getTransactions({
            startDate,
            endDate,
            limit: 50,
          });
          // Defensive: ensure we always have an array
          setTransactions(Array.isArray(txns) ? txns : (txns as any)?.data || (txns as any)?.transactions || []);
        } catch {
          setTransactions([]);
        }

        // Load history (once)
        if (!history) {
          try {
            const histResp = await api.get('/client/dashboard/expenses/history');
            if (histResp.data.success) setHistory(histResp.data.data);
          } catch { /* ignore */ }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentMonth, currentYear]);

  // Generate smart tips
  const smartTips = useMemo(
    () => generateSmartTips(expenses, previousExpenses, transactions),
    [expenses, previousExpenses, transactions]
  );

  // Calculate stats
  const stats = useMemo(() => {
    if (!expenses) return null;

    // Só mostrar variação % se mês anterior tiver valor > 0; caso contrário é N/A
    const monthlyChange = (previousExpenses && previousExpenses.total > 0)
      ? ((expenses.total - previousExpenses.total) / previousExpenses.total) * 100
      : null;

    const avgPerDay = (expenses.total || 0) / new Date(currentYear, currentMonth, 0).getDate();
    const categorias = expenses.categorias || [];
    const topCategory = categorias.length > 0
      ? categorias.reduce((max, cat) => (cat.valor > max.valor ? cat : max))
      : null;

    // Usar total_transacoes da API (mais fiável que contar o array de transações recentes)
    const transactionCount = expenses.totalTransacoes
      || (Array.isArray(transactions) ? transactions.length : 0);

    return {
      total: expenses.total || 0,
      monthlyChange,
      avgPerDay,
      topCategory,
      categoryCount: categorias.length,
      transactionCount,
    };
  }, [expenses, previousExpenses, transactions, currentMonth, currentYear]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoaderComponent size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="text-center p-8">
          <FiAlertCircle className="text-4xl text-red-500 mx-auto mb-4" />
          <p className="text-pagali-gray-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary-dark"
          >
            Tentar novamente
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-pagali-gray-800">Financas</h1>
          <p className="text-pagali-gray-500">
            {history?.totals ? `Membro desde ${history.totals.primeiroAno} — ${history.totals.anosAtivo} anos no Pagali` : 'Acompanhe e gerencie as suas despesas'}
          </p>
        </div>
        {activeTab === 'mensal' && (
          <MonthSelector
            month={currentMonth}
            year={currentYear}
            onChange={(m, y) => { setCurrentMonth(m); setCurrentYear(y); }}
          />
        )}
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-2 bg-pagali-gray-100 p-1 rounded-xl w-fit">
        <button onClick={() => setActiveTab('mensal')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'mensal' ? 'bg-white text-pagali-primary shadow-sm' : 'text-pagali-gray-500'}`}>
          Análise Mensal
        </button>
        <button onClick={() => setActiveTab('historico')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'historico' ? 'bg-white text-pagali-primary shadow-sm' : 'text-pagali-gray-500'}`}>
          Historico Completo
        </button>
      </div>

      {/* History Tab */}
      {activeTab === 'historico' && history && history.totals && Array.isArray(history.yearly) && Array.isArray(history.monthly) && (
        <>
          {/* Lifetime KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard title="Gasto Total" value={formatCurrency(history.totals.gastoTotal)}
              subtitle={`desde ${history.totals.primeiroAno}`}
              icon={<FiDollarSign className="text-xl" />} color="bg-gradient-to-br from-purple-500 to-purple-600" />
            <StatCard title="Total Transações" value={String(history.totals.totalTransacoes)}
              subtitle={`${history.totals.anosAtivo} anos`}
              icon={<FiBarChart2 className="text-xl" />} color="bg-gradient-to-br from-blue-500 to-blue-600" />
            <StatCard title="Média Anual" value={formatCurrency(history.totals.mediaAnual)}
              icon={<FiCalendar className="text-xl" />} color="bg-gradient-to-br from-green-500 to-green-600" />
            <StatCard title="Média Mensal" value={formatCurrency(history.totals.mediaMensal)}
              subtitle="últimos 12 meses"
              icon={<FiTrendingUp className="text-xl" />} color="bg-gradient-to-br from-amber-500 to-amber-600" />
          </div>

          {/* Yearly evolution chart */}
          <Card>
            <h2 className="text-lg font-semibold text-pagali-gray-800 mb-4 flex items-center gap-2">
              <FiTrendingUp className="text-pagali-primary" /> Evolução Anual
            </h2>
            <div className="space-y-3">
              {history.yearly.map((y, i) => {
                const maxGasto = Math.max(...history.yearly.map(yr => yr.gasto));
                const pct = maxGasto > 0 ? (y.gasto / maxGasto) * 100 : 0;
                const prev = i > 0 ? history.yearly[i - 1] : null;
                const growth = prev && prev.gasto > 0 ? ((y.gasto - prev.gasto) / prev.gasto * 100) : null;
                return (
                  <div key={y.ano} className="flex items-center gap-3">
                    <span className="text-sm font-bold text-pagali-gray-700 w-12">{y.ano}</span>
                    <div className="flex-1">
                      <div className="w-full bg-pagali-gray-100 rounded-full h-6 relative overflow-hidden">
                        <div className="h-6 rounded-full transition-all duration-700 flex items-center px-2"
                          style={{ width: `${Math.max(pct, 3)}%`, backgroundColor: '#6B46C1' }}>
                          {pct > 20 && <span className="text-xs text-white font-medium truncate">{formatCurrency(y.gasto)}</span>}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-pagali-gray-500 w-16 text-right">{y.transacoes} tx</span>
                    {growth !== null && (
                      <span className={`text-xs font-semibold w-14 text-right ${growth >= 0 ? 'text-red-500' : 'text-green-500'}`}>
                        {growth > 0 ? '+' : ''}{growth.toFixed(0)}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Monthly trend last 12 months */}
          <Card>
            <h2 className="text-lg font-semibold text-pagali-gray-800 mb-4 flex items-center gap-2">
              <FiBarChart2 className="text-pagali-primary" /> Ultimos 12 Meses
            </h2>
            <div className="flex items-end gap-1 h-40">
              {history.monthly.map((m) => {
                const maxM = Math.max(...history.monthly.map(x => x.gasto));
                const h = maxM > 0 ? (m.gasto / maxM) * 100 : 0;
                const label = m.mes.split('-')[1];
                const months = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
                return (
                  <div key={m.mes} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full rounded-t-md transition-all duration-500" title={`${formatCurrency(m.gasto)} — ${m.transacoes} tx`}
                      style={{ height: `${Math.max(h, 2)}%`, backgroundColor: '#6B46C1', opacity: 0.7 + (h / 100) * 0.3 }} />
                    <span className="text-[10px] text-pagali-gray-400">{months[parseInt(label) - 1]}</span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Top categories all time */}
          <Card>
            <h2 className="text-lg font-semibold text-pagali-gray-800 mb-4 flex items-center gap-2">
              <FiPieChart className="text-pagali-primary" /> Top Categorias (Histórico Completo)
            </h2>
            {(history.topCategories?.length ?? 0) > 0 ? (
              <div className="space-y-3">
                {(history.topCategories || []).map((cat, i) => {
                  const maxCat = history.topCategories?.[0]?.gasto || 1;
                  const pct = (cat.gasto / maxCat) * 100;
                  return (
                    <div key={i}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-pagali-gray-700">{cat.name}</span>
                        <span className="text-sm text-pagali-gray-500">{formatCurrency(cat.gasto)}</span>
                      </div>
                      <div className="w-full bg-pagali-gray-100 rounded-full h-2.5">
                        <div className="h-2.5 rounded-full transition-all duration-500"
                          style={{ width: `${pct}%`, backgroundColor: getCategoryColor(cat.name) }} />
                      </div>
                      <p className="text-xs text-pagali-gray-400 mt-0.5">{cat.transacoes} transacoes</p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-pagali-gray-400 text-center py-4">Sem dados historicos</p>
            )}
          </Card>
        </>
      )}

      {/* History Tab - Empty state */}
      {activeTab === 'historico' && (!history || !history.totals || !Array.isArray(history.yearly)) && (
        <Card>
          <div className="text-center py-12">
            <FiBarChart2 className="text-5xl text-pagali-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-pagali-gray-700 mb-2">Sem histórico disponível</h3>
            <p className="text-sm text-pagali-gray-500">Não existem dados históricos para este utilizador.</p>
          </div>
        </Card>
      )}

      {/* Monthly Tab */}
      {activeTab === 'mensal' && (
      <>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Gasto"
          value={formatCurrency(stats?.total || 0)}
          subtitle={`${currentMonth}/${currentYear}`}
          icon={<FiDollarSign className="text-xl" />}
          trend={stats?.monthlyChange != null && stats.monthlyChange !== 0
            ? (stats.monthlyChange > 0 ? 'up' : 'down')
            : 'neutral'}
          trendValue={stats?.monthlyChange != null
            ? `${Math.abs(stats.monthlyChange).toFixed(1)}% vs mês anterior`
            : 'sem dados do mês anterior'}
          color="bg-gradient-to-br from-purple-500 to-purple-600"
        />
        <StatCard
          title="Média Diária"
          value={formatCurrency(stats?.avgPerDay || 0)}
          subtitle="por dia"
          icon={<FiBarChart2 className="text-xl" />}
          color="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Maior Categoria"
          value={stats?.topCategory?.categoria || '-'}
          subtitle={stats?.topCategory ? formatCurrency(stats.topCategory.valor) : undefined}
          icon={<FiPieChart className="text-xl" />}
          color="bg-gradient-to-br from-amber-500 to-amber-600"
        />
        <StatCard
          title="Transações"
          value={String(stats?.transactionCount || 0)}
          subtitle="este mês"
          icon={<FiTrendingUp className="text-xl" />}
          color="bg-gradient-to-br from-green-500 to-green-600"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category Breakdown */}
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-pagali-gray-800">Despesas por Categoria</h2>
            <FiPieChart className="text-pagali-primary" />
          </div>

          {expenses?.categorias && expenses.categorias.length > 0 ? (
            <div className="space-y-4">
              {expenses.categorias
                .sort((a, b) => b.valor - a.valor)
                .map((cat, idx) => (
                  <CategoryBar
                    key={idx}
                    categoria={cat.categoria}
                    valor={cat.valor}
                    percentagem={cat.percentagem}
                    total={expenses.total}
                  />
                ))}
            </div>
          ) : (
            <div className="text-center py-8 text-pagali-gray-500">
              <FiPieChart className="text-4xl mx-auto mb-2 opacity-50" />
              <p>Sem dados de despesas para este periodo</p>
            </div>
          )}
        </Card>

        {/* Smart Tips */}
        <Card>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-pagali-gray-800">Dicas Inteligentes</h2>
            <HiLightBulb className="text-amber-500" />
          </div>

          <div className="space-y-4">
            {smartTips.length > 0 ? (
              smartTips.map((tip) => (
                <div
                  key={tip.id}
                  className={`p-4 rounded-xl border-l-4 ${
                    tip.type === 'success'
                      ? 'bg-green-50 border-green-500'
                      : tip.type === 'warning'
                      ? 'bg-amber-50 border-amber-500'
                      : 'bg-blue-50 border-blue-500'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-xl">{tip.icon}</div>
                    <div>
                      <h4 className="font-medium text-pagali-gray-800">{tip.title}</h4>
                      <p className="text-sm text-pagali-gray-600 mt-1">{tip.message}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-pagali-gray-500">
                <HiLightBulb className="text-3xl mx-auto mb-2 opacity-50" />
                <p className="text-sm">Adicione mais transacoes para receber dicas personalizadas</p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Monthly Comparison */}
      {previousExpenses && expenses && (
        <Card>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-pagali-gray-800">Comparação Mensal</h2>
            <FiBarChart2 className="text-pagali-primary" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Current Month */}
            <div className="p-4 bg-pagali-gray-50 rounded-xl">
              <p className="text-sm text-pagali-gray-500 mb-2">Mes Atual</p>
              <p className="text-2xl font-bold text-pagali-gray-800">{formatCurrency(expenses.total)}</p>
              <p className="text-xs text-pagali-gray-400 mt-1">
                {expenses.categorias?.length || 0} categorias
              </p>
            </div>

            {/* Previous Month */}
            <div className="p-4 bg-pagali-gray-50 rounded-xl">
              <p className="text-sm text-pagali-gray-500 mb-2">Mes Anterior</p>
              <p className="text-2xl font-bold text-pagali-gray-800">{formatCurrency(previousExpenses.total)}</p>
              <p className="text-xs text-pagali-gray-400 mt-1">
                {previousExpenses.categorias?.length || 0} categorias
              </p>
            </div>
          </div>

          {/* Difference */}
          <div className="mt-4 p-4 rounded-xl border border-pagali-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-pagali-gray-600">Diferenca</span>
              <span className={`font-bold ${
                expenses.total > previousExpenses.total ? 'text-red-500' : 'text-green-500'
              }`}>
                {expenses.total > previousExpenses.total ? '+' : '-'}
                {formatCurrency(Math.abs(expenses.total - previousExpenses.total))}
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <h2 className="text-lg font-semibold text-pagali-gray-800 mb-4">Acoes Rapidas</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => window.location.href = '/transactions'}
            className="p-4 bg-pagali-gray-50 rounded-xl hover:bg-pagali-gray-100 transition-colors text-center"
          >
            <FiBarChart2 className="text-2xl text-pagali-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-pagali-gray-700">Ver Historico</p>
          </button>
          <button
            onClick={() => window.location.href = '/payments'}
            className="p-4 bg-pagali-gray-50 rounded-xl hover:bg-pagali-gray-100 transition-colors text-center"
          >
            <FiDollarSign className="text-2xl text-green-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-pagali-gray-700">Pagar Servico</p>
          </button>
          <button
            onClick={() => window.location.href = '/wallet'}
            className="p-4 bg-pagali-gray-50 rounded-xl hover:bg-pagali-gray-100 transition-colors text-center"
          >
            <FiTarget className="text-2xl text-purple-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-pagali-gray-700">Minha Carteira</p>
          </button>
          <button
            onClick={() => window.location.href = '/profile'}
            className="p-4 bg-pagali-gray-50 rounded-xl hover:bg-pagali-gray-100 transition-colors text-center"
          >
            <FiCheckCircle className="text-2xl text-blue-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-pagali-gray-700">Meu Perfil</p>
          </button>
        </div>
      </Card>
      </>
      )}
    </div>
  );
};

export default Insights;
